import { z, type ZodType } from 'zod'
import {
  PRESENTATIONAL_FIELD_TYPES,
  type FieldVisibility,
  type FormField,
  type VisibilityCondition,
} from './form'

/**
 * Dynamic form validation, generated from `form_fields`. This module is pure
 * (no I/O) and lives in `@napaicp/schemas` on purpose: the backend `/validate`
 * route and the frontend Form Renderer's `zodResolver` must apply exactly the
 * same rule, or a field hidden by one and accepted by the other becomes a way
 * to slip an answer past validation.
 */

/**
 * Only what schema generation actually reads. Both the domain `FormField` the
 * backend maps from D1 and the `FormFieldDto` the frontend fetches over the
 * wire satisfy this without an adapter — deliberately, so the renderer can
 * pass its fetched fields straight in.
 */
export type FormFieldAnswerSpec = Pick<
  FormField,
  'key' | 'type' | 'required' | 'validation' | 'options' | 'visibility'
>

function conditionMatches(
  condition: VisibilityCondition,
  answers: Record<string, unknown>,
): boolean {
  const actual = answers[condition.fieldKey]
  switch (condition.operator) {
    case 'equals':
      return actual === condition.value
    case 'not_equals':
      return actual !== condition.value
  }
}

/** `null` visibility means always visible. */
export function evaluateVisibility(
  visibility: FieldVisibility,
  answers: Record<string, unknown>,
): boolean {
  if (visibility === null) {
    return true
  }
  const results = visibility.conditions.map((condition) => conditionMatches(condition, answers))
  return visibility.operator === 'AND' ? results.every(Boolean) : results.some(Boolean)
}

/**
 * PR-272/UX-280 (Student Participant UI/UX/IA v4.45): exported so the
 * Submission Workspace's completion-summary/section-status derivation
 * (`apps/web/src/features/submissions/derive-submission-sections.ts`) can
 * answer "is this field's current value present" using the exact same
 * predicate `buildFormAnswersSchema` below already builds `required` checks
 * on, rather than a second, potentially-disagreeing definition of "answered"
 * (e.g. treating an empty string or empty array as present).
 */
export function isPresent(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false
  }
  if (typeof value === 'string' && value.length === 0) {
    return false
  }
  if (Array.isArray(value) && value.length === 0) {
    return false
  }
  return true
}

/** The Zod schema for one field's own value, ignoring required-ness and visibility. */
function fieldValueSchema(field: FormFieldAnswerSpec): ZodType {
  const rules = field.validation

  switch (field.type) {
    case 'short_text':
    case 'long_text':
    case 'rich_text': {
      let schema = z.string()
      if (rules?.minLength !== undefined) {
        schema = schema.min(rules.minLength)
      }
      if (rules?.maxLength !== undefined) {
        schema = schema.max(rules.maxLength)
      }
      if (rules?.pattern !== undefined && rules.pattern.length > 0) {
        schema = schema.regex(new RegExp(rules.pattern))
      }
      return schema
    }
    case 'number': {
      let schema = z.number()
      if (rules?.min !== undefined) {
        schema = schema.min(rules.min)
      }
      if (rules?.max !== undefined) {
        schema = schema.max(rules.max)
      }
      return schema
    }
    case 'email':
      return z.string().trim().toLowerCase().pipe(z.email())
    case 'phone': {
      let schema = z.string().min(1)
      if (rules?.pattern !== undefined && rules.pattern.length > 0) {
        schema = schema.regex(new RegExp(rules.pattern))
      }
      return schema
    }
    case 'url':
      return z.url()
    case 'date':
      return z.iso.date()
    case 'datetime':
      return z.iso.datetime()
    case 'radio':
    case 'select': {
      const values = (field.options ?? []).map((option) => option.value)
      return values.length > 0 ? z.enum(values as [string, ...string[]]) : z.string().min(1)
    }
    case 'checkbox':
    case 'multi_select': {
      const values = (field.options ?? []).map((option) => option.value)
      const item = values.length > 0 ? z.enum(values as [string, ...string[]]) : z.string().min(1)
      return z.array(item)
    }
    case 'boolean':
      return z.boolean()
    case 'file':
    case 'image':
    case 'pdf':
    case 'zip': {
      let schema = z.string().min(1)
      const extensions = rules?.allowedExtensions
      if (extensions !== undefined && extensions.length > 0) {
        schema = schema.refine(
          (value) =>
            extensions.some((extension) => value.toLowerCase().endsWith(extension.toLowerCase())),
          `File must have one of these extensions: ${extensions.join(', ')}.`,
        )
      }
      return schema
    }
    case 'heading':
    case 'description':
    case 'separator':
      return z.never()
  }
}

/**
 * One Zod schema for a whole answers object, built from a form version's
 * fields. Enforces, per visible field: required-ness and the field's own type
 * rules. Enforces, per hidden field: that no value was supplied at all — a
 * value present for a field whose visibility condition is not currently met
 * is rejected outright, so hiding a field on the client is never load-bearing
 * for security (`01-master-context.md` invariant #6; the Phase 3 brief calls
 * this out explicitly for conditional fields).
 */
export function buildFormAnswersSchema(
  fields: readonly FormFieldAnswerSpec[],
): ZodType<Record<string, unknown>, Record<string, unknown>> {
  const answerable = fields.filter((field) => !PRESENTATIONAL_FIELD_TYPES.includes(field.type))

  return z.record(z.string(), z.unknown()).superRefine((answers, ctx) => {
    for (const field of answerable) {
      const visible = evaluateVisibility(field.visibility, answers)
      const provided = Object.prototype.hasOwnProperty.call(answers, field.key)
      const value = answers[field.key]

      if (!visible) {
        if (provided && value !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: [field.key],
            message:
              'This field is not visible under the current answers and must not be submitted.',
          })
        }
        continue
      }

      // AC-364: a required boolean is a "must be checked" field (a
      // declaration/consent), so an explicit `false` is as missing as no value.
      const missingBoolean = field.type === 'boolean' && value !== true
      if (!isPresent(value) || (field.required && missingBoolean)) {
        if (field.required) {
          ctx.addIssue({ code: 'custom', path: [field.key], message: 'This field is required.' })
        }
        continue
      }

      const result = fieldValueSchema(field).safeParse(value)
      if (!result.success) {
        for (const issue of result.error.issues) {
          ctx.addIssue({ code: 'custom', path: [field.key, ...issue.path], message: issue.message })
        }
      }
    }
  })
}
